/**
 * Barra de ações para seleção múltipla de arquivos
 * Exibe ações disponíveis quando arquivos são selecionados
 */

import React from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import DeleteIcon from '@mui/icons-material/Delete'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import DeselectIcon from '@mui/icons-material/Deselect'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import { FileItem } from '../../../services/api'
import { isComparisonEnabled } from '../../../utils/betaLimits'
import { useBetaMode } from '../../../contexts'

export type BatchAction = 'compare' | 'delete' | 'moveToFolder'

type Props = {
  selectedFiles: FileItem[]
  totalFiles: number
  onSelectAll: () => void
  onClearSelection: () => void
  onAction: (action: BatchAction) => void
  onClose: () => void
}

export default function SelectionToolbar({
  selectedFiles,
  totalFiles,
  onSelectAll,
  onClearSelection,
  onAction,
  onClose
}: Props) {
  const count = selectedFiles.length
  const allSelected = count === totalFiles && totalFiles > 0
  const completedCount = selectedFiles.filter(f => f.status === 'completed').length

  // Comparação requer 2-4 arquivos completos (controlada por BETA_LIMITS.comparisonEnabled)
  const canCompare = isComparisonEnabled() && completedCount >= 2 && completedCount <= 4
  // Delete sempre disponível
  const canDelete = count >= 1
  const betaMode = useBetaMode()

  return (
    <Slide direction="up" in={count > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          left: { xs: 8, sm: '50%' },
          right: { xs: 8, sm: 'auto' },
          transform: { xs: 'none', sm: 'translateX(-50%)' },
          zIndex: 1300,
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
          minWidth: { xs: 'auto', sm: 400, md: 500 },
          maxWidth: { xs: 'none', sm: 700 }
        }}
      >
        <Box
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <Chip
              label={count}
              size="small"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                height: { xs: 24, sm: 28 }
              }}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {count === 1 ? 'selecionado' : 'selecionados'}
            </Typography>
            {completedCount < count && (
              <Chip
                label={`${completedCount} OK`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.65rem',
                  height: 20,
                  display: { xs: 'none', sm: 'flex' }
                }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Tooltip title={allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}>
              <IconButton
                size="small"
                onClick={allSelected ? onClearSelection : onSelectAll}
                sx={{ color: 'white' }}
              >
                {allSelected ? <DeselectIcon /> : <SelectAllIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar Seleção">
              <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fff' }}>
          <Stack
            direction={{ xs: 'column', sm: 'column', md: 'row' }}
            spacing={{ xs: 1, sm: 1, md: 2 }}
            justifyContent="center"
            alignItems="stretch"
          >
            {/* Linha superior: Mover e Comparar */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="center"
              sx={{ flex: { xs: 'initial', sm: 'initial', md: 1 }, width: { xs: '100%', sm: '100%', md: 'auto' } }}
            >
              {/* Mover para pasta */}
              <Tooltip title="Mover arquivos selecionados para uma pasta">
                <span style={{ flex: 1, display: 'flex' }}>
                  <Button
                    variant="contained"
                    startIcon={<DriveFileMoveIcon />}
                    onClick={() => onAction('moveToFolder')}
                    size="small"
                    sx={{
                      flex: 1,
                      bgcolor: '#1565c0',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 1 },
                      '&:hover': { bgcolor: '#0d47a1' },
                      '& .MuiButton-startIcon': {
                        mr: { xs: 0.5, sm: 1 }
                      }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Mover</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Mover</Box>
                    <Box component="span" sx={{ ml: 0.5 }}>({count})</Box>
                  </Button>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  betaMode
                    ? 'A comparação entre arquivos não está disponível nesta versão de avaliação'
                    : !canCompare
                      ? completedCount < 2
                        ? 'Selecione 2-4 arquivos completos para comparar'
                        : 'Máximo de 4 arquivos para comparação'
                      : 'Comparar transcrições selecionadas'
                }
              >
                <span style={{ flex: 1, display: 'flex' }}>
                  <Button
                    variant="contained"
                    startIcon={<CompareArrowsIcon />}
                    disabled={!canCompare}
                    onClick={() => onAction('compare')}
                    size="small"
                    sx={{
                      flex: 1,
                      bgcolor: '#2e7d32',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.75, sm: 1 },
                      '&:hover': { bgcolor: '#1b5e20' },
                      '& .MuiButton-startIcon': {
                        mr: { xs: 0.5, sm: 1 }
                      }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Comparar</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Comp.</Box>
                    <Box component="span" sx={{ ml: 0.5 }}>({completedCount})</Box>
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {/* Divider - horizontal em mobile */}
            <Divider
              orientation="horizontal"
              sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
            />

            {/* Excluir */}
            <Tooltip title="Excluir arquivos selecionados">
              <span style={{ display: 'flex', flex: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!canDelete}
                  onClick={() => onAction('delete')}
                  size="small"
                  sx={{
                    flex: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.75, sm: 1 },
                    '& .MuiButton-startIcon': {
                      mr: { xs: 0.5, sm: 1 }
                    }
                  }}
                >
                  Excluir ({count})
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>
    </Slide>
  )
}
